import { instanceKey } from "@/lib/instanceKey";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import type {
  DecideSchemaChangesBody,
  DecideSchemaChangesResult,
  SchemaChangeTableCounts,
  SchemaChangeView,
  SchemaChangesQuery,
  SchemaPolicyBody,
  SetSchemaPolicyResult,
} from "@shared/types/schemaChanges";
import { apiRequest, apiRequestEnvelope } from "@/lib/api";
import { listQueryOptions } from "@/lib/listQuery";
import { RUNS_PATH } from "./use-runs";
import { dataflowPath, useInvalidateDataflows } from "./use-dataflows";

/**
 * The schema change policy and the per-table schema change log (product gaps row 7), as the
 * connection screens read and write them. The web only records decisions; the next load of the
 * table carries them out, so every write here invalidates the log and the summary and nothing else
 * of the run history.
 */

export const SCHEMA_CHANGES_SUMMARY_PATH = "/api/v2/schema-changes/summary";

export function schemaChangesPath(dataflowId: string, query: SchemaChangesQuery): string {
  const params = new URLSearchParams({
    status: query.status,
    page: String(query.page),
    pageSize: String(query.pageSize),
  });
  if (query.entity) params.set("entity", query.entity);
  if (query.run) params.set("run", query.run);
  return `${dataflowPath(dataflowId)}/schema-changes?${params.toString()}`;
}

/** Open counts per table of every connection. Empty is the common case. */
export function useSchemaChangeSummary(enabled = true): UseQueryResult<SchemaChangeTableCounts[]> {
  const { selectedInstance } = useTenantInstance();
  return useQuery<SchemaChangeTableCounts[]>({
    queryKey: instanceKey(SCHEMA_CHANGES_SUMMARY_PATH, selectedInstance?.id),
    enabled,
  });
}

export interface SchemaChangePage {
  items: SchemaChangeView[];
  page: number;
  pageSize: number;
  total: number;
}

/** One page of a connection's log, one table or all. Keyed under the connection's own path. */
export function useSchemaChanges(
  dataflowId: string | null,
  query: SchemaChangesQuery,
  enabled = true,
): UseQueryResult<SchemaChangePage> {
  return useQuery<SchemaChangePage>({
    ...listQueryOptions,
    queryKey: [
      dataflowId ? dataflowPath(dataflowId) : "schema-changes:none",
      "schema-changes",
      query,
    ],
    enabled: enabled && Boolean(dataflowId),
    queryFn: async ({ signal }) => {
      const envelope = await apiRequestEnvelope<SchemaChangeView[]>(
        schemaChangesPath(dataflowId as string, query),
        { signal },
      );
      return {
        items: envelope.data ?? [],
        page: envelope.meta?.page ?? query.page,
        pageSize: envelope.meta?.pageSize ?? query.pageSize,
        total: envelope.meta?.total ?? 0,
      };
    },
    placeholderData: keepPreviousData,
  });
}

function useInvalidateSchema() {
  const queryClient = useQueryClient();
  const invalidateDataflows = useInvalidateDataflows();
  return (dataflowId: string) => {
    void queryClient.invalidateQueries({ queryKey: [SCHEMA_CHANGES_SUMMARY_PATH] });
    invalidateDataflows(dataflowId);
  };
}

/**
 * `PUT /dataflows/:id/schema-policy`. A 409 `SCHEMA_POLICY_CONFIRM` means new columns are held;
 * the caller asks, then sends `applyWaiting: true`.
 */
export function useSetSchemaPolicy(
  dataflowId: string,
): UseMutationResult<SetSchemaPolicyResult, Error, SchemaPolicyBody> {
  const invalidate = useInvalidateSchema();
  return useMutation({
    mutationFn: (body: SchemaPolicyBody) =>
      apiRequest<SetSchemaPolicyResult>(`${dataflowPath(dataflowId)}/schema-policy`, {
        method: "PUT",
        body,
      }),
    onSuccess: () => invalidate(dataflowId),
  });
}

/** `POST /dataflows/:id/schema-changes/decide`. */
export function useDecideSchemaChanges(
  dataflowId: string,
): UseMutationResult<DecideSchemaChangesResult, Error, DecideSchemaChangesBody> {
  const invalidate = useInvalidateSchema();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: DecideSchemaChangesBody) =>
      apiRequest<DecideSchemaChangesResult>(`${dataflowPath(dataflowId)}/schema-changes/decide`, {
        method: "POST",
        body,
      }),
    onSuccess: (result) => {
      invalidate(dataflowId);
      if (result.runId) void queryClient.invalidateQueries({ queryKey: [RUNS_PATH] });
    },
  });
}
